import { skipToken } from '@reduxjs/toolkit/query/react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import {
  useGetMatterDocumentPreviewQuery,
  useGetMatterDocumentsQuery,
} from '@/services/api/mattersService';
import type { CoverageCategory } from '@/services/types/coverage-types';
import type { DocumentInstanceDto } from '@/services/types/document-types';

interface AutoGeneratedDocumentsProps {
  matterId: string;
  coverageCategory: CoverageCategory | null;
}

const isFirstPartyLorCategory = (t: string): boolean =>
  t === 'LOR_FIRST_PARTY';
const isThirdPartyLorCategory = (t: string): boolean =>
  t === 'LOR_THIRD_PARTY';

const doesLoRTemplateMatchCoverageCategory = (
  d: DocumentInstanceDto,
  category: CoverageCategory | null,
): boolean => {
  const t = d.document_template?.category ?? '';
  if (category === 'FIRST_PARTY') {
    return isFirstPartyLorCategory(t);
  }
  if (category === 'THIRD_PARTY') {
    return isThirdPartyLorCategory(t);
  }
  // All other categories (including FIRST_PARTY_HEALTH) are not eligible
  return false;
};

export const AutoGeneratedDocuments: React.FC<AutoGeneratedDocumentsProps> = ({
  matterId,
  coverageCategory,
}) => {
  const isEligibleCategory =
    coverageCategory === 'FIRST_PARTY' || coverageCategory === 'THIRD_PARTY';

  const shouldFetchDocuments = isEligibleCategory;
  const { data: docs = [], isFetching } = useGetMatterDocumentsQuery(matterId, {
    skip: !shouldFetchDocuments,
  });

  const autoDocs = React.useMemo(() => {
    return (docs || [])
      .filter(
        (d) =>
          !!d.auto_generated &&
          doesLoRTemplateMatchCoverageCategory(d, coverageCategory),
      )
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [docs, coverageCategory]);

  const [previewDoc, setPreviewDoc] =
    React.useState<DocumentInstanceDto | null>(null);

  const shouldFetchPreview = isEligibleCategory && previewDoc !== null;
  const previewArgs =
    shouldFetchPreview && previewDoc
      ? { matter_id: matterId, document_id: previewDoc.id }
      : skipToken;
  const { data: previewBlob, isFetching: isPreviewFetching } =
    useGetMatterDocumentPreviewQuery(previewArgs);
  const previewUrl = React.useMemo(
    () => (previewBlob ? URL.createObjectURL(previewBlob.blob) : null),
    [previewBlob],
  );
  React.useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const backendHost =
    (import.meta as any).env?.VITE_APP_DJANGO_BACKEND_HOST || '';

  if (!isEligibleCategory) {
    return null;
  }

  if (isFetching) {
    return (
      <div
        className="space-y-2 animate-pulse"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="h-4 bg-slate-200 rounded w-56" />
        <div className="h-3 bg-slate-200 rounded w-72" />
        <div className="h-3 bg-slate-200 rounded w-60" />
      </div>
    );
  }

  if (!autoDocs.length) {
    return (
      <div className="text-xs text-slate-500">
        No auto-generated Letters of Representation.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-900">
          Auto-generated Letters of Representation
        </h4>
      </div>

      <ul className="divide-y divide-gray-100">
        {autoDocs.map((doc) => {
          const title =
            doc.document_template?.name || 'Letter of Representation';
          const timestamp = new Date(doc.created_at).toLocaleString();

          const isLoadingThisPreview =
            isPreviewFetching && previewDoc?.id === doc.id;

          return (
            <li
              key={doc.id}
              className="py-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div
                  className="text-sm font-medium text-gray-900 truncate"
                  title={title}
                >
                  {title}
                </div>
                <div
                  className="text-xs text-slate-500"
                  aria-label="Generated at"
                >
                  {timestamp}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  onClick={() => setPreviewDoc(doc)}
                  disabled={isLoadingThisPreview}
                  aria-busy={isLoadingThisPreview}
                  aria-label={
                    isLoadingThisPreview
                      ? `Loading preview for ${title}`
                      : `Preview ${title}`
                  }
                >
                  {isLoadingThisPreview ? 'Loading…' : 'Preview'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const downloadUrl = `${backendHost}/api/matters/${matterId}/documents/${doc.id}/download/`;
                    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
                  }}
                  aria-label={`Download ${title}`}
                >
                  Download
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      <Dialog
        open={!!previewDoc}
        onOpenChange={(open) => !open && setPreviewDoc(null)}
      >
        <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
          <DialogTitle className="sr-only">
            Letter of Representation (Preview)
          </DialogTitle>
          {previewUrl ? (
            <iframe
              title="LoR Preview"
              src={previewUrl}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-slate-500 text-sm">
              Loading preview…
            </div>
          )}
        </RightDialogContent>
      </Dialog>
    </div>
  );
};
